import type firebase from "firebase/app";

import { firebaseConfig } from "@/config";
import { env } from "@/env";

import {
  getMaintenanceParameterKeys,
  MaintenanceConfig,
  parseMaintenanceSchedule,
  resolveMaintenanceEnvironment,
} from "./schedule";

const MINIMUM_FETCH_INTERVAL_MS = 5 * 60 * 1000;

const keys = getMaintenanceParameterKeys(
  resolveMaintenanceEnvironment(env.maintenanceEnv, env.backendUrl)
);

let remoteConfigPromise: Promise<firebase.remoteConfig.RemoteConfig> | null = null;

// Firebase is imported lazily so the SDK stays out of the initial bundle.
const getRemoteConfig = () => {
  if (!remoteConfigPromise) {
    remoteConfigPromise = (async () => {
      const { default: app } = await import("@/lib/firebase");
      await import("firebase/remote-config");
      const remoteConfig = app.remoteConfig();
      remoteConfig.settings.minimumFetchIntervalMillis = MINIMUM_FETCH_INTERVAL_MS;
      remoteConfig.defaultConfig = { [keys.enabled]: false, [keys.schedule]: "" };
      return remoteConfig;
    })();
    remoteConfigPromise.catch(() => {
      remoteConfigPromise = null;
    });
  }
  return remoteConfigPromise;
};

/** Null when Firebase is not configured or unreachable; never throws. */
export const loadMaintenanceConfig = async (): Promise<MaintenanceConfig | null> => {
  if (!firebaseConfig.apiKey || !firebaseConfig.projectId || !firebaseConfig.appId) return null;
  try {
    const remoteConfig = await getRemoteConfig();
    await remoteConfig.fetchAndActivate();
    return {
      enabled: remoteConfig.getValue(keys.enabled).asBoolean(),
      schedule: parseMaintenanceSchedule(remoteConfig.getValue(keys.schedule).asString()),
    };
  } catch {
    return null;
  }
};
